
import { defineComponent, ref, reactive, PropType, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import allApi from '@/api/allApi'
import moment from 'moment'
import lottie from 'lottie-web'
import { getLatestQuantGoldHistoryDate, QUANT_GOLD_STAT_PERIODS } from './quantGoldMock'
import { isXpClient } from '@/utils/xpClient'
import { isLargeFontMode } from '@/utils/pageZoom'

const quantGoldScanningAnimation = require('@/assets/shishi.json')
// 股票项接口
interface StockItem {
  Id: string
  Code: string
  Name: string
  MarketCode: string
}

export default defineComponent({
  name: 'FilterPanel',
  props: {
    updateTime: {
      type: String,
      default: ''
    },
    historyDates: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    theme: {
      type: String,
      default: 'light'
    },
    showFilterBtn: {
      type: Boolean,
      default: true
    },
    showStockCount: {
      type: Boolean,
      default: false
    },
    stockCount: {
      type: Number,
      default: 0
    },
    // 筹码大师新增 props
    showStockSearch: {
      type: Boolean,
      default: false
    },
    showSelectedStockInfo: {
      type: Boolean,
      default: false
    },
    selectedStockCount: {
      type: Number,
      default: 0
    },
    selectedStockDescription: {
      type: String,
      default: ''
    },
    signalTabs: {
      type: Object as PropType<{ latest: string; history: string }>,
      default: () => ({ latest: '最新信号', history: '历史信号' })
    },
    // 是否显示历史日期选择（筹码大师模式下不显示）
    showHistoryDateSelect: {
      type: Boolean,
      default: true
    },
    // 盟主趋势新增 props
    showOptionalFilter: {
      type: Boolean,
      default: false
    },
    mainTabs: {
      type: Object as PropType<{ signalPool: { key: string; label: string }; hotCases: { key: string; label: string } }>,
      default: null
    },
    currentMainTab: {
      type: String,
      default: 'signalPool'
    },
    isMengzhuTrend: {
      type: Boolean,
      default: false
    },
    isDragonSignal: {
      type: Boolean,
      default: false
    },
    isDragonHunter: {
      type: Boolean,
      default: false
    },
    dragonHunterMainTab: {
      type: String,
      default: 'newChose'
    },
    dragonHunterSubTab: {
      type: String,
      default: 'dragonStar'
    },
    dragonHunterSelectedStock: {
      type: Object as PropType<any>,
      default: null
    },
    isQuantGold: {
      type: Boolean,
      default: false
    },
    quantGoldMainTab: {
      type: String,
      default: 'newChose'
    },
    quantGoldSubTab: {
      type: String,
      default: 'entrySignal'
    },
    showAiScanning: {
      type: Boolean,
      default: false
    },
    quantGoldHistoryDate: {
      type: String,
      default: ''
    },
    quantGoldHotCasePeriod: {
      type: Number,
      default: 2
    }
  },
  emits: ['filter-change', 'date-change', 'optional-change', 'signal-type-change', 'refresh', 'reset-optional', 'stock-select', 'stock-dblclick', 'main-tab-change', 'dragon-hunter-sub-tab-change', 'dragon-hunter-optional-change', 'dragon-hunter-stock-search', 'dragon-hunter-search-clear', 'quant-gold-sub-tab-change', 'quant-gold-filter-change', 'quant-gold-date-change', 'quant-gold-hot-case-period-change'],
  setup(props, { emit }) {
    const route = useRoute()
    const filterPanelRef = ref<HTMLElement | null>(null)
    const hotCaseSelectKey = ref(0)
    const isXpOs = computed(() => isXpClient(route.query as Record<string, unknown>))
    const isLargeFont = computed(() => isLargeFontMode(route.query as Record<string, unknown>))

    const datePickerDropdownClass = computed(() => {
      const base = `${props.theme === 'dark' ? 'dark-date-picker' : 'light-date-picker'} custom-date-picker`
      return isLargeFont.value ? `${base} font-large-zoom-compensate` : base
    })

    const filterPopoverClass = computed(() => {
      const base = 'filter-popover'
      return isLargeFont.value ? `${base} font-large-zoom-compensate` : base
    })

    const commonFilterBtnRef = ref<HTMLElement | null>(null)
    const quantGoldFilterBtnRef = ref<HTMLElement | null>(null)

    const LARGE_FONT_CALENDAR_SELECTOR = '.ant-calendar-picker-container.font-large-zoom-compensate, .rc-calendar-picker-container.font-large-zoom-compensate'
    const LARGE_FONT_FILTER_SELECTOR = '.ant-popover.filter-popover.font-large-zoom-compensate'

    const alignLargeFontPopup = (
      triggerEl: HTMLElement | null,
      popupSelector: string,
      align: 'left' | 'right'
    ) => {
      if (!triggerEl || !isLargeFont.value) return
      const popupEl = document.querySelector(popupSelector) as HTMLElement | null
      if (!popupEl) return
      const rect = triggerEl.getBoundingClientRect()
      popupEl.style.position = 'fixed'
      const popupRect = popupEl.getBoundingClientRect()
      popupEl.style.left = align === 'right'
        ? `${rect.right - popupRect.width}px`
        : `${rect.left}px`
      popupEl.style.top = `${rect.bottom}px`
    }

    const adjustFilterPopoverToViewport = () => {
      const popover = document.querySelector('.filter-popover') as HTMLElement
      if (!popover) return
      const rect = popover.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      if (rect.right > viewportWidth - 5) {
        popover.style.right = '5px'
        popover.style.left = 'auto'
      }
    }

    const alignFilterPopover = (triggerEl: HTMLElement | null) => {
      if (isLargeFont.value) {
        alignLargeFontPopup(triggerEl, LARGE_FONT_FILTER_SELECTOR, 'right')
        return
      }
      adjustFilterPopoverToViewport()
    }

    const handleDatePickerOpenChange = (open: boolean, triggerSelector: string) => {
      if (!open || !isLargeFont.value) return
      nextTick(() => {
        requestAnimationFrame(() => {
          const trigger = document.querySelector(triggerSelector) as HTMLElement
          alignLargeFontPopup(trigger, LARGE_FONT_CALENDAR_SELECTOR, 'left')
        })
      })
    }

    const cleanupFloatingPopups = () => {
      document.querySelectorAll(
        'body > .ant-select-dropdown, body > .ant-calendar-picker-container, body > .rc-calendar-picker-container'
      ).forEach((node) => {
        node.parentNode?.removeChild(node)
      })
    }

    const getHotCaseSelectPopupContainer = () => document.body

    const handleHotCasePeriodDropdownVisibleChange = (visible: boolean) => {
      if (!visible) {
        setTimeout(cleanupFloatingPopups, 0)
      }
    }

    const handleQuantGoldHotCasePeriodNativeChange = (event: Event) => {
      const value = Number((event.target as HTMLSelectElement).value)
      if (!Number.isFinite(value)) return
      handleQuantGoldHotCasePeriodChange(value)
    }

    watch(
      () => props.quantGoldMainTab,
      (tab, prevTab) => {
        if (!props.isQuantGold || prevTab === tab) return
        cleanupFloatingPopups()
        if (tab === 'hotCase') {
          nextTick(() => {
            hotCaseSelectKey.value += 1
          })
        }
      }
    )
    const signalType = ref('latest') // 'latest' 或 'history'
    const selectedDate = ref('')

    const showQuantGoldFilter = ref(false)
    const quantGoldFilters = reactive({
      upSpaceStars: [5, 4, 3] as number[],
      plateHeat: 'all' as string
    })
    const quantGoldPlateOptions = [
      { value: 'all', label: '全部' },
      { value: 'top10', label: '热度Top10' },
      { value: 'top20', label: '热度Top20' },
      { value: 'top50', label: '热度Top50' }
    ]
    const isQuantGoldFilterActive = computed(() => {
      const allStarsSelected =
        quantGoldFilters.upSpaceStars.length === 3 &&
        [5, 4, 3].every(s => quantGoldFilters.upSpaceStars.includes(s))
      return !allStarsSelected || quantGoldFilters.plateHeat !== 'all'
    })
    /** 最新入选 / 查看往期：显示自选与筛选；数据追踪、热门案例不显示 */
    const quantGoldShowListActions = computed(() => {
      return props.isQuantGold &&
        (props.quantGoldMainTab === 'newChose' || props.quantGoldMainTab === 'beforeChose')
    })
    const toggleQuantGoldStar = (star: number) => {
      const idx = quantGoldFilters.upSpaceStars.indexOf(star)
      if (idx >= 0) {
        quantGoldFilters.upSpaceStars.splice(idx, 1)
      } else {
        quantGoldFilters.upSpaceStars.push(star)
      }
    }
    const toggleQuantGoldFilter = () => { showQuantGoldFilter.value = true }

    watch(showQuantGoldFilter, (visible) => {
      if (!visible) return
      nextTick(() => {
        requestAnimationFrame(() => alignFilterPopover(quantGoldFilterBtnRef.value))
      })
    })

    const handleQuantGoldFilterReset = () => {
      quantGoldFilters.upSpaceStars = [5, 4, 3]
      quantGoldFilters.plateHeat = 'all'
    }
    const handleQuantGoldFilterConfirm = () => {
      emit('quant-gold-filter-change', {
        upSpaceStars: quantGoldFilters.upSpaceStars.slice(),
        plateHeat: quantGoldFilters.plateHeat
      })
      showQuantGoldFilter.value = false
    }
    const handleQuantGoldSubTabChange = (subTab: string) => {
      if (subTab === 'exitSignal') {
        showQuantGoldFilter.value = false
      }
      emit('quant-gold-sub-tab-change', subTab)
    }
    const handleQuantGoldDateChange = (date: string) => {
      emit('quant-gold-date-change', date)
    }

    const quantGoldSelectedDateMoment = computed({
      get() {
        return props.quantGoldHistoryDate ? moment(props.quantGoldHistoryDate, 'YYYY-MM-DD') : null
      },
      set(val: any) {
        const dateStr = val ? (typeof val === 'string' ? val : val.format('YYYY-MM-DD')) : ''
        if (dateStr) handleQuantGoldDateChange(dateStr)
      }
    })

    const quantGoldDisabledDate = (current: any) => {
      if (!current) return false
      const dateStr = current.format('YYYY-MM-DD')
      const allowed = props.historyDates || []
      if (!allowed.length) return false
      return !allowed.includes(dateStr)
    }

    const handleQuantGoldHotCasePeriodChange = (period: number) => {
      emit('quant-gold-hot-case-period-change', period)
    }

    const quantGoldScanningRef = ref<HTMLElement | null>(null)
    let quantGoldScanningAnim: ReturnType<typeof lottie.loadAnimation> | null = null

    const destroyQuantGoldScanningAnim = () => {
      if (quantGoldScanningAnim) {
        quantGoldScanningAnim.destroy()
        quantGoldScanningAnim = null
      }
    }

    const mountQuantGoldScanningAnim = () => {
      if (!props.isQuantGold || props.quantGoldMainTab !== 'newChose') return
      if (!quantGoldScanningRef.value) return
      destroyQuantGoldScanningAnim()
      quantGoldScanningAnim = lottie.loadAnimation({
        container: quantGoldScanningRef.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: quantGoldScanningAnimation
      })
    }

    watch(
      () => [props.isQuantGold, props.quantGoldMainTab] as const,
      () => {
        nextTick(() => {
          if (props.isQuantGold && props.quantGoldMainTab === 'newChose') {
            mountQuantGoldScanningAnim()
          } else {
            destroyQuantGoldScanningAnim()
          }
        })
      }
    )

    onMounted(() => {
      nextTick(() => {
        mountQuantGoldScanningAnim()
      })
    })

    onUnmounted(() => {
      destroyQuantGoldScanningAnim()
    })

    const showFilter = ref(false)
    const showHistoryPopover = ref(false)
    const onlyOptional = ref(false)

    // DatePicker 双向绑定：moment 对象 <-> 字符串
    const selectedDateMoment = computed({
      get() {
        return selectedDate.value ? moment(selectedDate.value, 'YYYY-MM-DD') : null
      },
      set(val: any) {
        selectedDate.value = val ? (typeof val === 'string' ? val : val.format('YYYY-MM-DD')) : ''
      }
    })

    // 禁用不在 dateOptions 中的日期
    const disabledDate = (current: any) => {
      if (!current) return false
      const dateStr = current.format('YYYY-MM-DD')
      return !dateOptions.value.includes(dateStr)
    }

    // 股票搜索相关
    const searchStock = ref<string | undefined>(undefined)
    const stockOptions = ref<StockItem[]>([])
    const searchHistory = ref<StockItem[]>([]) // 搜索历史记录
    const MAX_HISTORY_SIZE = 10 // 最大历史记录数量
    const hasSearched = ref(false) // 是否已经搜索过（用于显示"未找到匹配结果"）
    let searchTimer: any = null

    // 用于区分单击和双击的定时器和标志
    let stockClickTimer: ReturnType<typeof setTimeout> | null = null
    let lastClickTime = 0
    let lastClickItemId: string | null = null
    const CLICK_DELAY = 300 // 双击判定时间（毫秒）
    let pendingClickItem: StockItem | null = null // 待处理的点击项

    // 搜索下拉框控制
    const searchDropdownOpen = ref(false)
    const stockSearchRef = ref<any>(null)

    // 显示选项：如果没有搜索内容且有历史记录，显示历史；搜索无结果时不显示任何内容
    const displayOptions = computed(() => {
      // 如果有搜索结果，显示搜索结果
      if (stockOptions.value.length > 0) {
        return stockOptions.value
      }
      // 如果已经搜索过但没有结果，返回空数组（不显示任何内容）
      if (hasSearched.value) {
        return []
      }
      // 如果没有搜索过且有历史记录，显示历史
      if (searchHistory.value.length > 0) {
        return searchHistory.value
      }
      // 否则返回空数组
      return []
    })

    // 判断当前是否在显示历史记录模式（没有搜索过且有历史记录）
    const isShowingHistory = computed(() => {
      return !hasSearched.value && stockOptions.value.length === 0 && searchHistory.value.length > 0
    })

    // 信号标签文本
    const signalTabLabels = computed(() => {
      return props.signalTabs || { latest: '最新信号', history: '历史信号' }
    })

    // 历史日期选项使用 props.historyDates
    const dateOptions = computed(() => {
      console.log('FilterPanel dateOptions 计算, historyDates:', props.historyDates)
      return props.historyDates
    })

    const filters = reactive({
      macd: 0,      // 趋势势能
      amount: 0,    // 资金标尺
      plate: 0      // 题材追踪
    })

    // 判断是否有筛选条件被选中（不是全部）
    const isFilterActive = computed(() => {
      return filters.macd !== 0 || filters.amount !== 0 || filters.plate !== 0
    })

    const setFilter = (type: string, value: number) => {
      filters[type] = value
    }

    const handleSignalTypeChange = (type: string) => {
      signalType.value = type
      // 切换信号类型时重置"只看自选"状态
      if (onlyOptional.value) {
        onlyOptional.value = false
        emit('optional-change', false)
      }
      if (type === 'latest') {
        selectedDate.value = ''
        showHistoryPopover.value = false
        emit('signal-type-change', type)
      } else {
        // 切换到历史信号模式时，先清空选中的日期
        selectedDate.value = ''
        // 触发事件获取交易日列表
        emit('signal-type-change', type)
        // 如果已有历史日期，默认选择第一个（仅在显示日期选择时）
        if (props.showHistoryDateSelect && props.historyDates && props.historyDates.length > 0) {
          selectedDate.value = props.historyDates[0]
          emit('date-change', selectedDate.value)
        }
      }
    }

    // 监听 historyDates 变化，当切换到历史信号模式且数据加载完成时自动选择第一个日期
    watch(() => props.historyDates, (newDates) => {
      console.log('FilterPanel watch historyDates:', newDates, 'signalType:', signalType.value, 'selectedDate:', selectedDate.value)
      if (props.showHistoryDateSelect && signalType.value === 'history' && newDates && newDates.length > 0 && !selectedDate.value) {
        selectedDate.value = newDates[0]
        emit('date-change', selectedDate.value)
      }
    }, { immediate: false })

    // 量化淘金查看往期：日历加载后自动选中最新日期（与四维猎手历史信号逻辑一致）
    watch(() => props.historyDates, (newDates) => {
      if (
        props.isQuantGold &&
        props.quantGoldMainTab === 'beforeChose' &&
        newDates?.length > 0 &&
        !props.quantGoldHistoryDate
      ) {
        handleQuantGoldDateChange(getLatestQuantGoldHistoryDate(newDates))
      }
    }, { immediate: false })

    // 监听一级 tab 切换，重置二级 tab 到"最新信号"
    watch(() => props.currentMainTab, () => {
      signalType.value = 'latest'
      selectedDate.value = ''
    })

    const selectHistoryDate = (date: string) => {
      selectedDate.value = date
      signalType.value = 'history'
      showHistoryPopover.value = false
      emit('date-change', date)
    }

    // 历史日期下拉选择变化
    const onHistoryDateSelect = (date: string) => {
      selectedDate.value = date
      emit('date-change', date)
    }

    const handleRefresh = () => {
      // 刷新时重置"只看自选"状态
      if (onlyOptional.value) {
        onlyOptional.value = false
        emit('optional-change', false)
      }
      emit('refresh')
    }

    const toggleOptional = () => {
      onlyOptional.value = !onlyOptional.value
      emit('optional-change', onlyOptional.value)
    }

    const handleReset = () => {
      filters.macd = 0
      filters.amount = 0
      filters.plate = 0
    }

    const handleConfirm = () => {
      // 题材筛选：如果是0则传'0'，否则传选中的值
      const tcValue = filters.plate === 0 ? '0' : String(filters.plate)
      emit('filter-change', {
        qs: filters.macd,    // 趋势筛选
        zj: filters.amount,  // 资金筛选
        tc: tcValue          // 题材筛选
      })
      showFilter.value = false
    }

    // 弹窗挂载到 body，避免被父容器 overflow 裁剪
    const getPopupContainer = () => {
      return document.body
    }

    // 阻止点击外部关闭弹窗，只能通过确定按钮关闭
    const handleFilterVisibleChange = (visible: boolean) => {
      // 只允许打开，不允许通过点击外部关闭
      if (visible) {
        showFilter.value = true
      }
      // 点击外部时 visible 为 false，但我们不处理，保持弹窗打开
    }

    // 手动打开筛选弹窗（只能通过确定按钮关闭）
    const toggleFilter = () => {
      showFilter.value = true
    }

    // 股票搜索（防抖处理）
    const handleStockSearch = (val: string) => {
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
      if (!val) {
        stockOptions.value = []
        hasSearched.value = false
        return
      }
      searchTimer = setTimeout(() => {
        const data: any = {}
        data.data = {
          markettype: ['CNX'],
          marketcode: 'sh,sz,bj',  // 支持沪深京A股
          key: val,
          active: false
        }
        data.headers = {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
        allApi.searchStock(data).then((res: any) => {
          stockOptions.value = (res.Data || []).filter((item: any) =>
            item.MarketCode === 'sh' || item.MarketCode === 'sz' || item.MarketCode === 'bj'
          )
          hasSearched.value = true // 标记已搜索
        }).catch(() => {
          stockOptions.value = []
          hasSearched.value = true // 标记已搜索
        })
      }, 300)
    }

    // 搜索框聚焦时显示历史记录
    const handleSearchFocus = (e: FocusEvent) => {
      // 只处理用户真正的点击事件，忽略程序触发的聚焦
      if (!e.isTrusted) {
        return
      }

      // 检查是否是从页面外部返回导致的聚焦（如双击打开新窗口后返回）
      // 通过检查 relatedTarget 是否为 null 且 document.hasFocus() 来判断
      if (!e.relatedTarget && document.hasFocus()) {
        // 可能是从其他窗口返回，延迟一下再判断
        setTimeout(() => {
          // 如果搜索框仍然是焦点，才打开下拉框
          const activeElement = document.activeElement
          if (activeElement && activeElement.closest('.stock-search-wrapper')) {
            searchDropdownOpen.value = true
          }
        }, 100)
        return
      }

      console.log('handleSearchFocus 被调用, searchStock:', searchStock.value, 'historyLength:', searchHistory.value.length)
      // 打开下拉框
      searchDropdownOpen.value = true
    }

    // 搜索框失焦时
    const handleSearchBlur = () => {
      // 延迟关闭，避免点击选项时下拉框提前关闭
      setTimeout(() => {
        if (!pendingClickItem) {
          searchDropdownOpen.value = false
        }
      }, 200)
    }

    // 下拉框显示状态变化
    const handleDropdownVisibleChange = (open: boolean) => {
      console.log('handleDropdownVisibleChange:', open)
      if (open) {
        searchDropdownOpen.value = true
      }
    }

    // 添加到搜索历史
    const addToHistory = (item: StockItem) => {
      // 移除已存在的相同股票
      const index = searchHistory.value.findIndex(h => h.Id === item.Id)
      if (index > -1) {
        searchHistory.value.splice(index, 1)
      }
      // 添加到最前面
      searchHistory.value.unshift(item)
      // 限制历史记录数量
      if (searchHistory.value.length > MAX_HISTORY_SIZE) {
        searchHistory.value.pop()
      }
    }

    // 从历史记录中删除指定项
    const removeFromHistory = (item: StockItem, event: Event) => {
      // 阻止事件冒泡，避免触发选择
      event.stopPropagation()
      event.preventDefault()
      // 从历史记录中移除
      const index = searchHistory.value.findIndex(h => h.Id === item.Id)
      if (index > -1) {
        searchHistory.value.splice(index, 1)
      }
      // 不关闭下拉框，保持打开状态
    }

    // 格式化股票名称：超过4个字符才显示省略号
    const formatStockName = (name: string) => {
      if (!name) return ''
      if (name.length <= 4) {
        return name
      }
      return name.substring(0, 4) + '...'
    }

    // 清空搜索输入
    const clearSearchInput = () => {
      console.log('clearSearchInput 被调用')
      // 关闭下拉框
      searchDropdownOpen.value = false
      // 立即清空值，不使用 nextTick（避免被 select 的内部逻辑覆盖）
      searchStock.value = undefined
      stockOptions.value = []
    }

    // 处理 a-select 的 select 事件（当选项被选中时触发）
    // 这个事件在 select 内部设置值之后触发，我们用它来立即清空值
    const handleOptionSelect = () => {
      // 立即清空选中的值，防止显示在输入框中
      nextTick(() => {
        searchStock.value = undefined
      })
    }

    // 处理单击事件
    const handleSingleClick = (item: StockItem) => {
      console.log('筹码大师搜索单击，触发 stock-select')
      // 添加到历史记录
      addToHistory(item)
      // 触发单击事件
      emit('stock-select', item)
      // 清空输入框（放在最后）
      clearSearchInput()
    }

    // 处理双击事件
    const handleDoubleClick = (item: StockItem) => {
      console.log('筹码大师搜索双击，触发 stock-dblclick')
      // 添加到历史记录
      addToHistory(item)
      // 触发双击事件
      emit('stock-dblclick', item)
      // 清空输入框（放在最后）
      clearSearchInput()
    }

    // 股票选择 - 使用时间间隔区分单击和双击
    const handleStockSelect = (item: StockItem) => {
      const currentTime = Date.now()
      const timeDiff = currentTime - lastClickTime

      console.log('handleStockSelect 被调用, item:', item.Code, 'timeDiff:', timeDiff, 'lastClickItemId:', lastClickItemId)

      // 判断是否为双击（同一个股票，且时间间隔小于300ms）
      if (lastClickItemId === item.Id && timeDiff < CLICK_DELAY) {
        // 双击：清除单击定时器，触发双击事件
        console.log('检测到双击')
        if (stockClickTimer) {
          clearTimeout(stockClickTimer)
          stockClickTimer = null
        }
        // 重置状态
        lastClickTime = 0
        lastClickItemId = null
        pendingClickItem = null
        // 触发双击
        handleDoubleClick(item)
        return
      }

      // 单击：记录点击信息，设置延迟执行
      lastClickTime = currentTime
      lastClickItemId = item.Id
      pendingClickItem = item

      // 清除之前的定时器
      if (stockClickTimer) {
        clearTimeout(stockClickTimer)
      }

      // 设置定时器，延迟执行单击
      stockClickTimer = setTimeout(() => {
        stockClickTimer = null
        // 只有当 pendingClickItem 存在时才触发单击
        if (pendingClickItem && pendingClickItem.Id === item.Id) {
          console.log('单击定时器触发')
          // handleSingleClick(item)  // 注释掉单击逻辑
        }
        // 重置状态
        lastClickTime = 0
        lastClickItemId = null
        pendingClickItem = null
      }, CLICK_DELAY)
    }

    // 清空选中的股票（保留以兼容旧代码，但现在不再使用）
    const handleStockClear = () => {
      clearSearchInput()
    }

    // 组件卸载时清除定时器
    onUnmounted(() => {
      if (stockClickTimer) {
        clearTimeout(stockClickTimer)
        stockClickTimer = null
      }
      if (searchTimer) {
        clearTimeout(searchTimer)
        searchTimer = null
      }
      document.removeEventListener('keydown', handleDragonHunterKeydown, true)
      _stopDragonHunterObserver()
    })

    // 盟主趋势顶级 Tab 切换
    const handleMainTabChange = (tab: string) => {
      if (props.isQuantGold) {
        cleanupFloatingPopups()
        if (tab === 'hotCase') {
          nextTick(() => {
            hotCaseSelectKey.value += 1
          })
        }
      }
      // 切换Tab时重置"只看自选"状态
      if (onlyOptional.value) {
        onlyOptional.value = false
        emit('optional-change', false)
      }
      // 龙头猎手切换一级 tab 时清空搜索框（保留历史）
      if (tab !== 'beforeChose') {
        dragonHunterDisplayValue.value = ''
        dragonHunterSearchValue.value = undefined
        dragonHunterOptions.value = []
        dragonHunterHasSearched.value = false
        dragonHunterOnlyOptional.value = false
      }
      emit('main-tab-change', tab)
    }

    // 龙头猎手二级 tab 切换
    const handleDragonHunterSubTabChange = (subTab: string) => {
      emit('dragon-hunter-sub-tab-change', subTab)
    }

    // 龙头猎手往期入选：只看自选
    const dragonHunterOnlyOptional = ref(false)
    const toggleDragonHunterOptional = () => {
      dragonHunterOnlyOptional.value = !dragonHunterOnlyOptional.value
      if (dragonHunterOnlyOptional.value) {
        dragonHunterSearchValue.value = undefined
        dragonHunterOptions.value = []
      }
      emit('dragon-hunter-optional-change', dragonHunterOnlyOptional.value)
    }

    // 龙头猎手往期入选：搜索个股
    const dragonHunterSearchValue = ref<string | undefined>(undefined)
    const dragonHunterOptions = ref<any[]>([])
    const dragonHunterDropdownOpen = ref(false)
    const dragonHunterSearchRef = ref<any>(null)
    let dragonHunterSearchTimer: any = null
    const dragonHunterHistory = ref<any[]>([]) // 搜索历史
    const dragonHunterHasSearched = ref(false)  // 是否已搜索过

    // 显示选项：有搜索结果显示结果，否则显示历史
    const dragonHunterDisplayOptions = computed(() => {
      if (dragonHunterOptions.value.length > 0) return dragonHunterOptions.value
      if (dragonHunterHasSearched.value) return []
      return dragonHunterHistory.value
    })

    const dragonHunterIsShowingHistory = computed(() =>
      !dragonHunterHasSearched.value && dragonHunterOptions.value.length === 0 && dragonHunterHistory.value.length > 0
    )

    const addToDragonHunterHistory = (item: any) => {
      const idx = dragonHunterHistory.value.findIndex(h => h.Id === item.Id || h.Code === item.Code)
      if (idx > -1) dragonHunterHistory.value.splice(idx, 1)
      dragonHunterHistory.value.unshift(item)
    }

    const removeFromDragonHunterHistory = (item: any, event: Event) => {
      event.stopPropagation()
      event.preventDefault()
      const idx = dragonHunterHistory.value.findIndex(h => h.Id === item.Id || h.Code === item.Code)
      if (idx > -1) dragonHunterHistory.value.splice(idx, 1)
    }

    const handleDragonHunterSearch = (val: string) => {
      if (dragonHunterSearchTimer) clearTimeout(dragonHunterSearchTimer)
      if (!val) {
        dragonHunterOptions.value = []
        dragonHunterHasSearched.value = false
        return
      }
      dragonHunterSearchTimer = setTimeout(() => {
        const data: any = {
          data: { markettype: ['CNX'], marketcode: 'sh,sz,bj', key: val, active: false },
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }
        allApi.searchStock(data).then((res: any) => {
          dragonHunterOptions.value = (res.Data || []).filter((item: any) =>
            ['sh', 'sz', 'bj'].includes(item.MarketCode)
          )
          dragonHunterHasSearched.value = true
        }).catch(() => {
          dragonHunterOptions.value = []
          dragonHunterHasSearched.value = true
        })
      }, 300)
    }

    // 上下键导航 + 回车确认
    // 上下键交给 a-select 原生处理（高亮+滚动），用 MutationObserver 同步当前高亮索引
    let _dragonHunterObserver: MutationObserver | null = null
    const dragonHunterActiveIndex = ref(-1)

    const _startDragonHunterObserver = () => {
      if (_dragonHunterObserver) return
      // 等下拉框渲染完再开始观察
      setTimeout(() => {
        const dropdown = document.querySelector('.dragon-hunter-dropdown') as HTMLElement | null
        if (!dropdown) return
        _dragonHunterObserver = new MutationObserver(() => {
          const items = Array.from(dropdown.querySelectorAll('.ant-select-item-option'))
          const idx = items.findIndex(el => el.classList.contains('ant-select-item-option-active'))
          dragonHunterActiveIndex.value = idx
        })
        _dragonHunterObserver.observe(dropdown, { subtree: true, attributes: true, attributeFilter: ['class'] })
      }, 100)
    }

    const _stopDragonHunterObserver = () => {
      if (_dragonHunterObserver) {
        _dragonHunterObserver.disconnect()
        _dragonHunterObserver = null
      }
      dragonHunterActiveIndex.value = -1
    }

    // 直接从 DOM 读取当前高亮项索引，兼容低版本浏览器（不依赖 MutationObserver 同步）
    const _getDragonHunterActiveIndexFromDOM = (): number => {
      const dropdown = document.querySelector('.dragon-hunter-dropdown') as HTMLElement | null
      if (!dropdown) return -1
      const items = Array.from(dropdown.querySelectorAll('.ant-select-item-option'))
      return items.findIndex(el => el.classList.contains('ant-select-item-option-active'))
    }

    const handleDragonHunterKeydown = (e: KeyboardEvent) => {
      const options = dragonHunterDisplayOptions.value

      if (e.key === 'ArrowDown' || e.key === 'Down' || e.key === 'ArrowUp' || e.key === 'Up') {
        // 不拦截，让 a-select 原生处理高亮移动和滚动
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()

        if (!dragonHunterDropdownOpen.value || options.length === 0) {
          _dragonHunterDoEnter()
          return
        }

        // 每次回车时直接从 DOM 实时读取高亮索引，兼容低版本浏览器
        const idx = _getDragonHunterActiveIndexFromDOM()
        if (idx >= 0 && idx < options.length) {
          handleDragonHunterStockClick(options[idx])
        } else {
          // 无高亮则选第一条
          handleDragonHunterStockClick(options[0])
        }
      }
    }

    // 回车时无下拉选项：立即发起搜索并选中第一条
    const _dragonHunterDoEnter = () => {
      const val = dragonHunterSearchValue.value
      if (!val) return
      if (dragonHunterSearchTimer) clearTimeout(dragonHunterSearchTimer)
      const data: any = {
        data: { markettype: ['CNX'], marketcode: 'sh,sz,bj', key: val, active: false },
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
      allApi.searchStock(data).then((res: any) => {
        const results = (res.Data || []).filter((item: any) =>
          ['sh', 'sz', 'bj'].includes(item.MarketCode)
        )
        dragonHunterOptions.value = results
        dragonHunterHasSearched.value = true
        dragonHunterActiveIndex.value = -1
        if (results.length > 0) {
          handleDragonHunterStockClick(results[0])
        }
      }).catch(() => {
        dragonHunterOptions.value = []
        dragonHunterHasSearched.value = true
        dragonHunterActiveIndex.value = -1
      })
    }

    // 已废弃，保留空函数避免引用报错
    const _scrollDragonHunterActiveIntoView = () => {}

    const handleDragonHunterFocus = () => {
      dragonHunterHasSearched.value = false
      dragonHunterDropdownOpen.value = true
      document.addEventListener('keydown', handleDragonHunterKeydown, true)
      _startDragonHunterObserver()
    }
    const handleDragonHunterBlur = () => {
      document.removeEventListener('keydown', handleDragonHunterKeydown, true)
      _stopDragonHunterObserver()
      setTimeout(() => { dragonHunterDropdownOpen.value = false }, 200)
    }
    const handleDragonHunterSelect = () => {
      nextTick(() => { dragonHunterSearchValue.value = undefined })
    }
    const handleDragonHunterStockClick = (item: any) => {
      dragonHunterOnlyOptional.value = false
      dragonHunterDropdownOpen.value = false
      dragonHunterSearchValue.value = undefined
      dragonHunterOptions.value = []
      dragonHunterHasSearched.value = false
      addToDragonHunterHistory(item)
      // 直接设置显示值，不依赖外部 prop 回传
      dragonHunterDisplayValue.value = `${item.Name || ''}(${item.Code || ''})`
      emit('dragon-hunter-stock-search', item)
    }

    // 监听外部传入的选中股票（表格行点击回填）
    watch(() => props.dragonHunterSelectedStock, (stock) => {
      if (stock) {
        dragonHunterDisplayValue.value = `${stock.name || stock.Name || ''}(${stock.symbol || stock.Code || ''})`
      } else {
        dragonHunterDisplayValue.value = ''
      }
    })

    // 龙头猎手往期：搜索框显示值（回填用）
    const dragonHunterDisplayValue = ref('')

    // 清空搜索框
    const handleDragonHunterClear = () => {
      dragonHunterDisplayValue.value = ''
      dragonHunterSearchValue.value = undefined
      dragonHunterOptions.value = []
      emit('dragon-hunter-search-clear')
    }

    // 重置只看自选状态（供外部调用）
    const resetOptional = () => {
      if (onlyOptional.value) {
        onlyOptional.value = false
        emit('optional-change', false)
      }
    }

    // 获取筛选图标（根据主题和激活状态）
    const getFilterIcon = () => {
      if (isFilterActive.value) {
        // 激活状态：黑版用 blackActive.png，白版用 VectorActic.png
        return props.theme === 'dark'
          ? require('@/assets/blackActive.png')
          : require('@/assets/VectorActic.png')
      } else {
        // 未激活状态：使用默认图标
        return require('@/assets/hot/image/Vector.png')
      }
    }

    watch(showFilter, (visible) => {
      if (!visible) return
      nextTick(() => {
        const run = () => alignFilterPopover(commonFilterBtnRef.value)
        if (isLargeFont.value) requestAnimationFrame(run)
        else setTimeout(run, 100)
      })
    })

    return {
      signalType,
      selectedDate,
      selectedDateMoment,
      disabledDate,
      dateOptions,
      showFilter,
      showHistoryPopover,
      onlyOptional,
      filters,
      isFilterActive,
      signalTabLabels,
      searchStock,
      stockOptions,
      displayOptions,
      searchHistory,
      searchDropdownOpen,
      stockSearchRef,
      hasSearched,
      setFilter,
      handleSignalTypeChange,
      selectHistoryDate,
      onHistoryDateSelect,
      handleRefresh,
      toggleOptional,
      handleReset,
      handleConfirm,
      getPopupContainer,
      handleFilterVisibleChange,
      toggleFilter,
      handleStockSearch,
      handleSearchFocus,
      handleSearchBlur,
      handleDropdownVisibleChange,
      handleOptionSelect,
      handleStockSelect,
      handleStockClear,
      handleMainTabChange,
      resetOptional,
      removeFromHistory,
      formatStockName,
      isShowingHistory,
      getFilterIcon,
      handleDragonHunterSubTabChange,
      dragonHunterOnlyOptional,
      toggleDragonHunterOptional,
      dragonHunterSearchValue,
      dragonHunterOptions,
      dragonHunterDropdownOpen,
      dragonHunterSearchRef,
      handleDragonHunterSearch,
      handleDragonHunterKeydown,
      handleDragonHunterFocus,
      handleDragonHunterBlur,
      handleDragonHunterSelect,
      handleDragonHunterStockClick,
      dragonHunterDisplayValue,
      handleDragonHunterClear,
      quantGoldShowListActions,
      showQuantGoldFilter,
      quantGoldFilters,
      quantGoldPlateOptions,
      isQuantGoldFilterActive,
      toggleQuantGoldStar,
      toggleQuantGoldFilter,
      handleQuantGoldFilterReset,
      handleQuantGoldFilterConfirm,
      handleQuantGoldSubTabChange,
      handleQuantGoldDateChange,
      filterPanelRef,
      hotCaseSelectKey,
      isXpOs,
      isLargeFont,
      datePickerDropdownClass,
      filterPopoverClass,
      commonFilterBtnRef,
      quantGoldFilterBtnRef,
      handleDatePickerOpenChange,
      getHotCaseSelectPopupContainer,
      handleHotCasePeriodDropdownVisibleChange,
      handleQuantGoldHotCasePeriodNativeChange,
      quantGoldSelectedDateMoment,
      quantGoldDisabledDate,
      QUANT_GOLD_STAT_PERIODS,
      handleQuantGoldHotCasePeriodChange,
      quantGoldScanningRef,
      dragonHunterDisplayOptions,
      dragonHunterIsShowingHistory,
      removeFromDragonHunterHistory
    }
  }
})
